import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import SearchFilters from '../../components/Search/SearchFilters';
import objectiveService, { Objective } from '../../services/objective.service';

const SearchObjectives: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [filteredObjectives, setFilteredObjectives] = useState<Objective[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load public objectives
  useEffect(() => {
    const loadObjectives = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await objectiveService.getAvailableObjectives();
        setObjectives(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((objective: Objective) => {
          objective.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (err) {
        console.error('Error loading objectives:', err);
        setError('Erreur lors du chargement des objectifs');
      } finally {
        setLoading(false);
      }
    };

    loadObjectives();
  }, []);

  // Filter objectives
  useEffect(() => {
    let filtered = [...objectives];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (objective) =>
          objective.title?.toLowerCase().includes(query) ||
          objective.description?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((objective) =>
        selectedTags.every((tag) => objective.tags?.includes(tag))
      );
    }

    setFilteredObjectives(filtered);
  }, [searchQuery, selectedTags, objectives]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="objectifs"
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredObjectives.length} objectif{filteredObjectives.length > 1 ? 's' : ''} trouvé{filteredObjectives.length > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredObjectives.map((objective) => (
                <Grid item xs={12} sm={6} md={4} key={objective.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {objective.title}
                      </Typography>

                      {objective.created_by_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Par {objective.created_by_name}
                        </Typography>
                      )}

                      {objective.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 1,
                          }}
                        >
                          {objective.description}
                        </Typography>
                      )}

                      {objective.objective_type && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Type: {objective.objective_type === 'skill' ? 'Savoir-faire' :
                                 objective.objective_type === 'course' ? 'Cours' :
                                 'Pattern d\'exercice'}
                        </Typography>
                      )}

                      {objective.tags && objective.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {objective.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredObjectives.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucun objectif ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchObjectives;
